"use client";

import React from "react";
import Image from "next/image";
import { MethodologySection } from "@/.storyblok/types/319751/storyblok-components";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, EffectFade } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/effect-fade";
import { SbBlokData, storyblokEditable } from "@storyblok/react";
import { renderRichText } from "@/lib/richText";

interface MethodologySectionProps {
  blok?: MethodologySection & SbBlokData;
}

const MethodologySectionComponent: React.FC<MethodologySectionProps> = ({ blok }) => {
  if (!blok) return null;

  const { headline, subtitle, items, use_bottom_line } = blok;

  const pagination = {
    clickable: true,
    renderBullet: function(index: number, className: string) {
      return `<span class="${className}" tabindex="0">${index + 1}</span>`;
    },
  };

  return (
    <>
      <section {...(blok && storyblokEditable(blok))} className="py-5 py-md-5">
        <div className="container">
          {headline && <h2 className="text-center">{headline}</h2>}
          {subtitle && (
            <div
              className="lead text-center mb-4"
              dangerouslySetInnerHTML={{ __html: renderRichText(subtitle) }}
            />
          )}
        </div>

        <div
          className="swiper two-thirds-centered methodology-and-service-slider swiper-fade swiper-initialized swiper-horizontal swiper-watch-progress swiper-backface-hidden">
          <Swiper
            modules={[Pagination, EffectFade]}
            effect="fade"
            pagination={pagination}
            watchSlidesProgress
            grabCursor
            fadeEffect={{
              crossFade: true,
            }}
            className="mb-0"
          >
            <div className="swiper-wrapper">
              {items?.map((item, index) => (
                <SwiperSlide
                  key={item._uid}
                  className="swiper-slide text-center pt-3"
                  role="group"
                  aria-label={`${index + 1} / ${items?.length}`}
                >
                  {item.icon?.filename ? (
                    <Image
                      src={item.icon.filename}
                      alt={item.title || ""}
                      width={100}
                      height={100}
                      className="mt-5 mb-4 mx-auto js-lazy-load"
                      sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                      loading="lazy"
                      quality={75}
                    />
                  ) : (
                    <div className="placeholder-image" style={{ height: "60px" }}></div>
                  )}
                  {item.title && (
                    <h3 className="dark-highight-text">{item.title}</h3>
                  )}
                  {item.description && (
                    <p className="lead mb-0 fw-bolder">{item.description}</p>
                  )}
                </SwiperSlide>
              ))}
            </div>
          </Swiper>
          <span className="swiper-notification" aria-live="assertive" aria-atomic="true"></span>
        </div>
      </section>
      {use_bottom_line && <hr />}
    </>
  );
};

export default MethodologySectionComponent;